import React from "react";
import { useState, useEffect, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";


import "./styles.css";

import Navbar from "./components/Navbar";
import CoursesSection from "./components/CourseSection";
import CircuitBackground from "./components/CircuitBackground";
import BannerCarousel from "./components/BannerCarousel";
import Timeline from "./components/Timeline";
import Testimonials from "./components/Testimonials";
import Faqdata from "./components/Faqdata";
import Footer from "./components/Footer.js";
import { SITE,  } from "./config/site";
import { useScrollReveal } from "./hooks/useScrollReveal";


function useCountUp(target, duration = 3500) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    let start = 0;
    const increment = target / (duration / 10);

    const timer = setInterval(() => {
      start += increment;

      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 10);

    return () => clearInterval(timer);
  }, [target, duration]);

  return {
    value: count,
    ref,
  };
}


const queryClient = new QueryClient();

const stats = [
  { value: 3500, suffix: "+", label: "Students Trained" },
  { value: 15, suffix: "+ Yrs", label: "Instruction Experience" },
  { value: 2100, suffix: "+", label: "Successful Students" },
  { value: 100, suffix: "%", label: "Placement Assistance" },
];

function Stat({ value, suffix, label, delay }) {
  const { value: count, ref } = useCountUp(value);
  return (
    <div
      className="stat-card scroll-reveal"
      data-reveal-delay={delay}
      ref={ref}
    >
      <div className="stat-label">{label}</div>

      <div className="stat-value">
        {count.toLocaleString()}
        <span className="stat-suffix">{suffix}</span>
      </div>
    </div>
  );
}

const whyChoose = [
  {
    t: "100% Practical Training",
    d: "Learn mobile repairing with real devices "
  },
  {
    t: "Industry Experts",
    d: "Get trained by experienced mobile repair engineers."
  },
  {
    t: "Latest Equipment",
    d: "Practice using professional tools and modern repair machines."
  },
  {
    t: "Placement Assistance",
    d: "Receive career guidance and placement support after training."
  },
  {
    t: "Business Support",
    d: "Learn how to start your own mobile repair business."
  },
  {
    t: "Lifetime Support",
    d: "Access technical guidance even after completing the course."
  }
];


function App() {
  const appRef = useRef(null);
  useScrollReveal(appRef);

  return (
    <QueryClientProvider client={queryClient}>
      <CircuitBackground />

      <div className="app-shell" ref={appRef}>
        <Navbar />

        <BannerCarousel />

        <section className="hero scroll-reveal">
          <div className="container-x hero-grid">
           <div className="hero-content">
              <div className="hero-badge scroll-reveal" data-reveal-delay="1">
                <span className="hero-badge-dot" />
                Admissions Open · 2026 Batch
              </div>

              <div className="hero-title-row">
              <h2 className="hero-title scroll-reveal" data-reveal-delay="2">
                Become a Pro Mobile Repair Engineer in{" "}
                <span className="text-primary">25 Days.</span>
              </h2>

              <div className="hero-actions scroll-reveal" data-reveal-delay="3">
                <button type="button" className="btn-primary-lg bg-primary">
                  Apply for Admission
                </button>

                <div className="hero-secondary-actions">
                  <a
                    href={SITE.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline-lg"
                  >
                    WhatsApp Now
                  </a>

                  <a href={SITE.phoneHref} className="btn-outline-lg">
                    Call {SITE.phone}
                  </a>
                </div>
              </div>
              
            </div>
               <p className="hero-desc scroll-reveal" data-reveal-delay="4">
                Master Android, iPhone and advanced chip-level micro-soldering.
                100% practical training in our high-tech Hyderabad lab — from
                industry veterans with 10+ years on the bench.
              </p>

              <div className="hero-social-proof scroll-reveal" data-reveal-delay="5">
                <div className="hero-avatars">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="hero-avatar" />
                  ))}
                </div>

                <div>
                  <span className="text-ink">5,000+ students</span> trained
                  across Telangana & AP
                </div>
              </div>
            </div>

          </div>
        </section>
        {/* STATS */}
        <section className="stats-section scroll-reveal">
          <div className="stats-container scroll-reveal" data-reveal-delay="1">
            {stats.map((stat, index) => (
              <Stat
                key={stat.label}
                value={stat.value}
                suffix={stat.suffix}
                label={stat.label}
                delay={index + 1}
              />
            ))}
          </div>
        </section>
        {/* WHY CHOOSE */}
          <section className="why-section scroll-reveal">
            <div className="why-container">

              <div className="why-header scroll-reveal" data-reveal-delay="1">
                <div className="scroll-reveal" data-reveal-delay="2">
                  <div className="why-subtitle">
                    Why SMT
                  </div>

                  <h2 className="why-title">
                    Built for technicians who want a real career, not a certificate.
                  </h2>
                </div>
              </div>

              <div className="why-grid scroll-reveal" data-reveal-delay="2">
                {whyChoose.map((item, index) => (
                  <div
                    key={item.t}
                    className="why-card scroll-reveal"
                    data-reveal-delay={(index % 6) + 1}
                  >

                    <div className="why-number">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    <h3 className="why-card-title">
                      {item.t}
                    </h3>

                    <p className="why-card-text">
                      {item.d}
                    </p>

                  </div>
                ))}
              </div>

            </div>
          </section>

          <CoursesSection/>
          <Timeline/>
          <Testimonials/>
          <Faqdata/>

          {/* CTA SECTION */}

            <section className="cta-section scroll-reveal">

            <div className="cta-container scroll-reveal">

                <h2 className="cta-title scroll-reveal">
                    Ready to build your career?
                </h2>

                <p className="cta-description scroll-reveal">
                    Limited seats in the next batch. Get a free professional
                    toolkit worth ₹5,000 on early registration.
                </p>

                <div className="cta-buttons scroll-reveal">

                    <a href="/apply" className="cta-btn-primary scroll-reveal">
                        Apply Now
                    </a>

                    <a
                        href={SITE.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cta-btn-outline"
                    >
                        WhatsApp
                    </a>

                    <a
                        href={SITE.phoneHref}
                        className="cta-btn-outline"
                    >
                        Call Now
                    </a>

                </div>

            </div>

            </section>
            <Footer/>
      </div>
      
    </QueryClientProvider>
  );
}

export default App;
